import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('vj_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('vj_session_id', sessionId);
  }
  return sessionId;
};

// Map paths to friendly page names
const getPageName = (pathname) => {
  const pathMappings = {
    '/': 'Landing Page',
    '/dashboard': 'Dashboard',
    '/trips/new': 'Criar Viagem',
    '/notifications': 'Notificações',
    '/settings': 'Configurações',
    '/admin': 'Admin Dashboard',
    '/admin/users': 'Admin Usuários',
    '/admin/trips': 'Admin Viagens',
    '/admin/analytics': 'Admin Analytics',
    '/admin/activity': 'Admin Atividades',
    '/admin/settings': 'Admin Configurações',
  };

  // Check for exact match first
  if (pathMappings[pathname]) {
    return pathMappings[pathname];
  }

  // Check for trip detail pages
  if (pathname.match(/^\/trip\/[^/]+$/)) {
    return 'Detalhes da Viagem';
  }
  if (pathname.match(/^\/trip\/[^/]+\/overview$/)) {
    return 'Visão Geral';
  }
  if (pathname.match(/^\/trip\/[^/]+\/itinerary$/)) {
    return 'Roteiro';
  }
  if (pathname.match(/^\/trip\/[^/]+\/payments$/)) {
    return 'Pagamentos';
  }
  if (pathname.match(/^\/trip\/[^/]+\/votes$/)) {
    return 'Votações';
  }
  if (pathname.match(/^\/trip\/[^/]+\/members$/)) {
    return 'Membros';
  }
  if (pathname.match(/^\/trip\/[^/]+\/carpools$/)) {
    return 'Caronas';
  }
  if (pathname.match(/^\/trip\/[^/]+\/purchases$/)) {
    return 'Compras';
  }
  if (pathname.match(/^\/trip\/[^/]+\/files$/)) {
    return 'Arquivos';
  }
  if (pathname.match(/^\/trip\/[^/]+\/gallery$/)) {
    return 'Galeria';
  }
  if (pathname.match(/^\/trip\/[^/]+\/checklist$/)) {
    return 'Checklist';
  }
  if (pathname.match(/^\/trip\/[^/]+\/reviews$/)) {
    return 'Avaliações';
  }
  if (pathname.match(/^\/trip\/[^/]+\/analytics$/)) {
    return 'Analytics da Viagem';
  }
  if (pathname.match(/^\/trip\/[^/]+\/settings$/)) {
    return 'Configurações da Viagem';
  }
  if (pathname.match(/^\/trip\/[^/]+\/faq$/)) {
    return 'FAQ';
  }
  if (pathname.match(/^\/trip\/[^/]+\/feedback$/)) {
    return 'Feedback';
  }
  if (pathname.match(/^\/trip\/[^/]+\/withdrawals$/)) {
    return 'Desistências';
  }
  if (pathname.match(/^\/trip\/[^/]+\/shopping-lists$/)) {
    return 'Listas de Compras';
  }

  // Default: use path as name
  return pathname;
};

/**
 * Hook to track page views automatically
 * Sends pageview data to the backend for analytics
 */
export const usePageTracking = () => {
  const location = useLocation();
  const { api, isAuthenticated } = useAuth();
  const pageLoadTime = useRef(Date.now());
  const lastPath = useRef(null);

  const trackPageView = useCallback(async (path, timeOnPage = null) => {
    try {
      const sessionId = getSessionId();
      const pageName = getPageName(path);

      await api.post('/admin/track-pageview', null, {
        params: {
          page: pageName,
          path: path,
          session_id: sessionId,
          time_on_page: timeOnPage
        }
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Pageview tracking failed:', error.message);
    }
  }, [api]);

  useEffect(() => {
    const currentPath = location.pathname;

    // Track time spent on previous page
    if (lastPath.current && lastPath.current !== currentPath) {
      const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
      // Only track if user spent at least 1 second on the page
      if (timeSpent >= 1) {
        trackPageView(lastPath.current, timeSpent);
      }
    }

    // Track new page view
    trackPageView(currentPath);
    
    // Reset timer and update last path
    pageLoadTime.current = Date.now();
    lastPath.current = currentPath;

    // Track time on page when user leaves
    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
      if (timeSpent >= 1) {
        // Use sendBeacon for reliability on page unload
        const sessionId = getSessionId();
        const pageName = getPageName(currentPath);
        const params = new URLSearchParams({
          page: pageName,
          path: currentPath,
          session_id: sessionId,
          time_on_page: timeSpent.toString()
        });
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        navigator.sendBeacon(`${backendUrl}/api/admin/track-pageview?${params}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, trackPageView]);
};

/**
 * Component wrapper for page tracking
 * Use this in App.js to enable automatic tracking
 */
export const PageTracker = ({ children }) => {
  usePageTracking();
  return children;
};

export default usePageTracking;
